import { EyeInvisibleOutlined } from '@ant-design/icons';
import { Alert, Checkbox, Heading, HelpText, Line, Link } from '@mm/components';
import {
  EMAIL_LINKED_TO_ANOTHER_ACCOUNT,
  UNPROCESSABLE_ENTITY,
} from '@mm/constants/status-codes';
import { UTMTags } from '@mm/hooks';
import { EyeLight } from '@mm/icons';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useFacebookSessionCRUDActions } from '@mm/state/actions/FacebookSessionCRUDActions';
import sessionCRUDActions from '@mm/state/actions/SessionCRUDActions';
import usersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import {
  LoginType,
  mapLoginTypeToPersona,
  Persona,
} from '@mm/state/controllers/SessionController';
import commonHandler from '@mm/state/errorHandlers/common-handler';
import JSONUtils from '@mm/state/helpers/json-utils';
import {
  OnboardingSignupStep,
  UserAttributes,
} from '@mm/state/model/CompanyUsersModel';
import onboardingActions from '@mm/state/modules/onboardingState/actions';
import { StoreState } from '@mm/state/store-interfaces';
import { getErrorJson, LocalStorageService } from '@mm/utils/index';
import { Spin } from 'antd';
import classNames from 'classnames';
import * as queryString from 'query-string';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import { selectIsShopifyContext } from '@mm/state/selectors/viewSelectors';
import FacebookLoginButton from '../../../../common/buttons/FacebookLoginButton/FacebookLoginButton';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PhoneInput } from '../../../../common/components/PhoneInput/PhoneInput';
import TextInput from '../../../../common/components/TextInput';
import { URL_REGEX } from '../../../../common/constants/validations';
import { PREV_LOCATION } from '../../../../common/helpers/routes/GuardedPrivateRoute';
import {
  EmailInput,
  useEmailInput,
} from '../../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import { useRegisterSignupEvent } from '../../../../dashboard/hooks/use-register-signup-event';
import { colors } from '../../../../editor/features/beta/styles/colors';
import { Routes } from '../../../../routes/top-level-routes';
import { routes } from '../../routes';
import { PasswordValidation } from '../PasswordValidation/PasswordValidation';
import { checkStringComplexity } from './checkStringComplexity';
import './SignUpForm.pcss';
import PrimarySelect, {
  DropdownOption,
} from '../../../../common/components/PrimarySelect/PrimarySelect';

export type SignUpFormPayload = {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone: string;
  companyName: string;
  website: string;
  smsConsent: boolean;
  businessType?: string;
  monthlyAdBudget?: string;
  websiteVisitors?: string;
  clientsCount?: string;
};

export type SignUpFormProps = {
  type?: LoginType;
  inviteToken?: string;
  affiliateId?: string;
  requiredOnlyFields?: boolean;
  showEmail?: boolean;
  showFacebook?: boolean;
  heading?: string;
  aboveHeading?: string | ReactNode;
  subheading?: string;
  invitingCompanyId?: number;
  invitingCompanyName?: string;
  css?: {
    form?: string;
    container?: string;
    heading?: string;
    aboveHeading?: string;
    subheading?: string;
    submitBtn?: string;
    helpText?: string;
  };
  hideTrial?: boolean;
  forXrayOnboarding?: boolean;
  withPhone?: boolean;
  withPasswordConfirmation?: boolean;
  signUpButtonName?: string;
  websiteURL?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  withCompanyName?: boolean;
  withPassword?: boolean;
  withWebsite?: boolean;
  withFirstName?: boolean;
  withLastName?: boolean;
  onSubmitSuccess?: () => void;
  hideAgencyCheckbox?: boolean;
  onSubmit?: (form: SignUpFormPayload) => void;
};

export const useSignup = (invitingCompanyId?: number, isShopify?: boolean) => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const history = useHistory();
  const { retrieveBasicListByCompany } = useChatbotCRUDActions();
  const stringifiedPrevLocation = LocalStorageService.getItem(PREV_LOCATION);
  const prevLocation = JSONUtils.safeParse<Location>(stringifiedPrevLocation);
  const parsedPreviousPathname = prevLocation?.pathname?.split('/');
  const isCustomPlan =
    parsedPreviousPathname?.[1] === 'subscribe' &&
    !!parsedPreviousPathname?.[2];

  const { registerSignUpEventWithTracking } = useRegisterSignupEvent();

  const signUpEmail = async (
    ...args: Parameters<typeof sessionCRUDActions.signUp>
  ) => {
    const [obj] = args;
    try {
      const { inviteToken } = obj;

      const session = await dispatch(
        sessionCRUDActions.signUp({
          ...obj,
          userAttributes: {
            ...obj.userAttributes,
            freeTrial: !isCustomPlan,
          },
        }),
      );

      if (inviteToken) {
        // clear token to avoid fetch bot data inner magic,
        // signup route takes care of invite
        await dispatch(sessionCRUDActions.resetInviteToken());
      }

      const companyId =
        invitingCompanyId || session?.userAttributes?.activeCompanyId;
      let activeBot;

      const invitedUser = inviteToken || invitingCompanyId;

      if (companyId) {
        activeBot = await retrieveBasicListByCompany(companyId);
        await dispatch(
          usersCRUDActions.updateActiveCompanyId(session.id, companyId),
        );
      }

      if (inviteToken || invitingCompanyId) {
        dispatch(onboardingActions.setJustCompletedOnboarding(true));
      }

      // set to affect modal open ordering
      if (!session.userAttributes.mmBuyBeforeTrying) {
        const chatbotId = activeBot?.[0]?.id;
        registerSignUpEventWithTracking({
          nextOnboardingStep: invitedUser
            ? OnboardingSignupStep.NONE
            : OnboardingSignupStep.USER_INFO,
          options: {
            session,
            companyId,
            chatbotId,
          },
        });
      }
    } catch (e) {
      if (e instanceof Response) {
        const responseErrorBody = await getErrorJson(e);
        if (
          e.status === UNPROCESSABLE_ENTITY &&
          responseErrorBody.error_code === EMAIL_LINKED_TO_ANOTHER_ACCOUNT &&
          responseErrorBody.errors
        ) {
          return responseErrorBody.errors[0];
        }
      }
      return commonHandler(dispatch)(e);
    }
  };

  const { create: createFBSession } = useFacebookSessionCRUDActions();
  const signUpFacebook = async ({
    facebookToken,
    inviteToken,
    userAttributes,
  }: {
    facebookToken: string;
    inviteToken?: string;
    userAttributes?: { signupForm?: Persona } & Pick<
      UserAttributes,
      'smsConsent'
    >;
  }) => {
    const opts = {
      facebookToken,
      inviteToken,
      userAttributes: {
        ...userAttributes,
        freeTrial: !isCustomPlan,
      },
    };

    const response = await createFBSession(opts);
    if (response && !response.error) {
      const path = response.userAttributes.mmBuyBeforeTrying
        ? Routes.subscribe.getLink()
        : Routes.chatbotEditor.path;
      history.push(path);
    }
  };

  return {
    signUpEmail,
    signUpFacebook,
    isCustomPlan,
  };
};

export const PASSWORD_LENGTH = 12;

export const usePasswordValidation = password =>
  useMemo(() => {
    const complexity = checkStringComplexity(password);
    return !!(
      complexity.total >= PASSWORD_LENGTH &&
      complexity.lowercase &&
      complexity.uppercase &&
      complexity.numbers &&
      complexity.specials
    );
  }, [password]);

// tslint:disable-next-line: cyclomatic-complexity
export const SignUpForm = ({
  css,
  inviteToken,
  requiredOnlyFields,
  heading = 'Sign up',
  aboveHeading,
  subheading = '',
  showEmail = true,
  showFacebook = true,
  type,
  invitingCompanyId,
  invitingCompanyName,
  hideTrial,
  forXrayOnboarding,
  withPhone = false,
  withPasswordConfirmation = true,
  signUpButtonName,
  websiteURL,
  hideAgencyCheckbox = false,
  email: initialEmail,
  firstName: initialFirstName,
  lastName: initialLastName,
  companyName: initialCompanyName,
  onSubmit: onSubmitProp,
  onSubmitSuccess,
  withCompanyName = false,
  withPassword = true,
  withFirstName = true,
  withLastName = true,
  withWebsite = false,
}: SignUpFormProps) => {
  const utmObj = UTMTags.get();
  const showRequiredOnlyFields = requiredOnlyFields || inviteToken;
  const signupForm = mapLoginTypeToPersona(type);
  const isSignUpForMobile = type === LoginType.mobile;
  const isHighLevel = type === LoginType.highLevel;
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordControl, setPasswordControl] = useState('');
  const [firstName, setFirstName] = useState(initialFirstName || '');
  const [lastName, setLastName] = useState(initialLastName || '');
  const [companyName, setCompanyName] = useState(
    invitingCompanyName || initialCompanyName || '',
  );
  const [website, setWebsite] = useState(websiteURL || '');
  const [phone, setPhone] = useState('');
  const [businessType, setBusinessType] = useState<DropdownOption | undefined>(
    undefined,
  );

  const hasAgencyUtmSource = utmObj?.utmSource === 'agency';
  const [agency, setAgency] = useState(
    hasAgencyUtmSource || isHighLevel || false,
  );
  const [useHighLevel, setUseHighLevel] = useState(isHighLevel || false);
  const [isSmsMarketingChecked, setIsSmsMarketingChecked] = useState(false);

  const isShopify = useSelector(selectIsShopifyContext);

  const campaignEmail = useSelector(
    (state: StoreState) => state?.signUpForm?.email,
  );
  const { emailState, onChange } = useEmailInput(
    initialEmail || campaignEmail || '',
  );

  const passwordMatch = password === passwordControl;
  const isPasswordValid = usePasswordValidation(password);

  const beenInvited = invitingCompanyId || inviteToken;

  const businessTypeOptions = [
    { id: 'dtc', name: 'Direct to Consumer' },
    { id: 'b2b', name: 'Business to Business' },
    { id: 'agency', name: 'Agency' },
  ] as DropdownOption[];

  const {
    location: { search },
  } = useRouter();

  let fieldsForValidation: (string | boolean)[] = [
    withPassword ? password : '',
    withFirstName ? firstName : '',
    withLastName ? lastName : '',
  ].filter(Boolean);

  if (!showRequiredOnlyFields) {
    fieldsForValidation = [
      ...fieldsForValidation,
      companyName,
      website && URL_REGEX.test(website),
      phone,
      businessType?.id.toString() ?? '',
    ];
  }

  if (withCompanyName && !beenInvited) {
    fieldsForValidation = [...fieldsForValidation, companyName];
  }

  if (withWebsite && !beenInvited) {
    fieldsForValidation = [
      ...fieldsForValidation,
      website && URL_REGEX.test(website),
    ];
  }

  const isValid =
    emailState.isValid &&
    (withPassword ? isPasswordValid : true) &&
    fieldsForValidation.every(field =>
      typeof field === 'string' ? field.length : Boolean(field),
    );

  const { signUpEmail, signUpFacebook, isCustomPlan } = useSignup(
    invitingCompanyId,
    isShopify,
  );

  useEffect(() => {
    if (type === LoginType.whiteLabel && search) {
      const { facebookToken } = queryString.parse(search);
      if (typeof facebookToken === 'string') {
        signUpFacebook({
          facebookToken,
          userAttributes: { smsConsent: isSmsMarketingChecked },
        });
      }
    }
  }, []);

  // to prevent UI flickering on rerender caused by token reset
  const isSigningUpWithToken = !!inviteToken || !!invitingCompanyId;

  const [showPasswordValidation, setShowPasswordValidation] = useState(false);
  const [accountError, setAccountError] = useState('');
  const complexity = checkStringComplexity(password);

  const onSubmit = async e => {
    e.preventDefault();

    if (onSubmitProp) {
      onSubmitProp({
        email: emailState.email,
        password,
        firstName,
        lastName,
        phone,
        companyName,
        website,
        smsConsent: isSmsMarketingChecked,
        ...(businessType && { businessType: businessType?.id.toString() }),
      });
      return;
    }

    const onboardingReportVersion = localStorage.getItem(
      'onboardingReportVersion',
    );
    setBusy(true);

    const res = await signUpEmail({
      email: emailState.email,
      password,
      firstName,
      lastName,
      phone,
      companyName,
      website,
      ...(businessType && { businessType: businessType?.id.toString() }),
      inviteToken,
      additionalFeatures: forXrayOnboarding ? ['xray_onboarding'] : undefined,
      userAttributes: {
        ...(forXrayOnboarding && { onboardingReportVersion }),
        ...((inviteToken || isShopify || invitingCompanyId) && {
          onboardingSignupStep: OnboardingSignupStep.NONE,
        }),
        signupForm,
        agency: hasAgencyUtmSource || agency,
        gohighlevel: useHighLevel,
        smsConsent: isSmsMarketingChecked,
        ...utmObj,
      },
    });
    setBusy(false);
    if (!res) {
      onSubmitSuccess?.();
    }

    if (res) {
      setAccountError(res);
    }
  };

  const [passwordVisible, setPasswordVisible] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  const signInLinkTo =
    type === LoginType.whiteLabel || inviteToken
      ? routes.signin.compile(type) + search
      : routes.signin.compile(type);

  const labelClassNames = (
    diabledCondition: boolean,
    isInput: boolean,
    isSelect?: boolean,
  ) =>
    classNames({
      'sign-up-form__input': isInput,
      'sign-up-form__input-invalid': isInput && diabledCondition && showErrors,
      'sign-up-form__input-label': !isInput,
      'sign-up-form__input-label-invalid':
        !isInput && diabledCondition && showErrors,
      'sign-up-form__select': isSelect,
    });

  useEffect(() => {
    if (isPasswordValid) {
      setShowPasswordValidation(false);
    }
  }, [isPasswordValid]);

  return (
    <Spin spinning={busy} size="small" tip="Processing...">
      {forXrayOnboarding
        ? !forXrayOnboarding
        : !isSignUpForMobile && (
            <HelpText
              className={classNames(
                'sign-up-form__footer-actions',
                'sign-up-form__footer-actions__login',
              )}
              text={
                <span>
                  Already have an account?{' '}
                  <Link className="sign-up-form__link-button" to={signInLinkTo}>
                    Log in
                  </Link>
                </span>
              }
            />
          )}
      <form onSubmit={onSubmit} className={css?.form}>
        <div className={classNames(css?.container, 'sign-up-form')}>
          {aboveHeading && (
            <div className={css?.aboveHeading}>{aboveHeading}</div>
          )}
          <Heading
            className={classNames(css?.heading, 'sign-up-form__heading')}
            size={4}
          >
            {heading}
          </Heading>

          {subheading && (
            <Heading
              className={classNames(
                css?.subheading,
                'sign-up-form__subheading',
              )}
              size={4}
            >
              {subheading}
            </Heading>
          )}
          {!!showFacebook && !isSignUpForMobile && (
            <FacebookLoginButton
              className="sign-up-form__facebook-btn"
              onSuccess={({ facebookToken }: { facebookToken: string }) =>
                signUpFacebook({
                  facebookToken,
                  inviteToken,
                  userAttributes: {
                    signupForm,
                    ...(!beenInvited && { freeTrial: !isCustomPlan }),
                    ...utmObj,
                  },
                })
              }
              label="Continue with Facebook"
            />
          )}

          {showFacebook && (
            <Line className="sign-up-form__separator" text="or with email" />
          )}

          {!!showEmail && (
            <>
              {accountError && (
                <Alert
                  className="sign-up-form__alert"
                  showCloseIcon={false}
                  type="error"
                >
                  {accountError}
                </Alert>
              )}
              <div className="sign-up-form__input-group-row">
                {withFirstName && (
                  <div className="sign-up-form__input-group">
                    <label className={labelClassNames(!firstName, false)}>
                      First Name
                    </label>

                    <TextInput
                      className={labelClassNames(!firstName, true)}
                      onChange={e => setFirstName(e.target.value)}
                      aria-label="First name"
                      type="text"
                      value={firstName}
                    />
                  </div>
                )}

                {withLastName && (
                  <div className="sign-up-form__input-group">
                    <label className={labelClassNames(!lastName, false)}>
                      Last Name
                    </label>
                    <TextInput
                      className={labelClassNames(!lastName, true)}
                      onChange={e => setLastName(e.target.value)}
                      aria-label="Last name"
                      type="text"
                      value={lastName}
                    />
                  </div>
                )}
              </div>
              {!isSigningUpWithToken && withWebsite && (
                <div className="sign-up-form__input-group">
                  <label className={labelClassNames(!website, false)}>
                    Company Website
                  </label>
                  <TextInput
                    className={labelClassNames(!website, true)}
                    onChange={e => setWebsite(e.target.value)}
                    aria-label="Business Website"
                    type="text"
                    placeholder="yourdomain.com"
                    value={website}
                  />
                </div>
              )}
              <div className="sign-up-form__input-group-row">
                <div
                  className={classNames('sign-up-form__input-group', {
                    'sign-up-form__input-group-full-width':
                      isSigningUpWithToken,
                  })}
                >
                  <label
                    className={labelClassNames(!emailState.isValid, false)}
                  >
                    {' '}
                    Work Email
                  </label>
                  <EmailInput
                    onChange={onChange}
                    inputProps={{
                      className: labelClassNames(!emailState.isValid, true),
                      placeholder: ' ',
                      'data-testid': 'mmEmailInput',
                    }}
                    withIcon={false}
                    strict
                    defaultValue={emailState.email}
                  />
                </div>
                {!isSigningUpWithToken && withPhone && (
                  <div className="sign-up-form__input-group">
                    <label className={labelClassNames(!phone, false)}>
                      {' '}
                      Phone
                    </label>
                    <PhoneInput
                      className={classNames('sign-up-form__phone-input', {
                        'sign-up-form__phone-input-invalid':
                          !phone && showErrors,
                      })}
                      value={phone}
                      onChange={val => setPhone(val)}
                    />
                  </div>
                )}
              </div>
              {!isSigningUpWithToken && (
                <div className="sign-up-form__input-group-row">
                  {withCompanyName && (
                    <div className="sign-up-form__input-group">
                      <label className={labelClassNames(!companyName, false)}>
                        Company Name
                      </label>
                      <TextInput
                        className={labelClassNames(!companyName, true)}
                        onChange={e => setCompanyName(e.target.value)}
                        aria-label="Company"
                        value={companyName}
                        type="text"
                      />
                    </div>
                  )}
                  {!isSigningUpWithToken && (
                    <div className="sign-up-form__input-group">
                      <label className={labelClassNames(!businessType, false)}>
                        Business Type
                      </label>
                      <PrimarySelect
                        className={labelClassNames(!businessType, false, true)}
                        value={businessType}
                        onChange={v => {
                          if (agency) {
                            setUseHighLevel(false);
                          }
                          setAgency(v.id === 'agency');
                          setBusinessType(v);
                        }}
                        options={businessTypeOptions}
                      />
                    </div>
                  )}
                </div>
              )}

              {withPassword && (
                <div
                  onFocus={() => setShowPasswordValidation(true)}
                  onBlur={() => setShowPasswordValidation(false)}
                  className="sign-up-form__input-group"
                >
                  <label className={labelClassNames(!isPasswordValid, false)}>
                    Password
                  </label>
                  <TextInput
                    className={labelClassNames(!isPasswordValid, true)}
                    onChange={e => setPassword(e.target.value)}
                    error={!!(password.length && !isPasswordValid)}
                    aria-label="password"
                    placeholder="Password"
                    type={passwordVisible[1] ? 'text' : 'password'}
                    autoComplete="off"
                  />
                  <span
                    className={classNames(
                      'sign-up-form__password-icon',
                      'first',
                      {
                        'sign-up-form__password-icon-error':
                          !isPasswordValid && showErrors,
                      },
                    )}
                    onClick={() =>
                      setPasswordVisible({
                        ...passwordVisible,
                        1: !passwordVisible[1],
                      })
                    }
                  >
                    {passwordVisible[1] ? (
                      <EyeLight color={colors.accentBlue} />
                    ) : (
                      <EyeInvisibleOutlined className="sign-up-form__eye-invisible " />
                    )}
                  </span>
                </div>
              )}

              {showPasswordValidation && (
                <div
                  className={classNames('sign-up-form__password-validation', {
                    'sign-up-form__password-validation-valid': isPasswordValid,
                  })}
                >
                  <PasswordValidation
                    text={`Contains at least ${PASSWORD_LENGTH} characters`}
                    error={password.length < PASSWORD_LENGTH}
                    childClassName={
                      forXrayOnboarding
                        ? 'sign-up-form__password-validation-error'
                        : ''
                    }
                    withIcon={false}
                    hideWithoutError
                  />
                  <PasswordValidation
                    text="Use upper & lower case letters"
                    error={complexity.lowercase < 1 || complexity.uppercase < 1}
                    childClassName={
                      forXrayOnboarding
                        ? 'sign-up-form__password-validation-error'
                        : ''
                    }
                    withIcon={false}
                    hideWithoutError
                  />
                  <PasswordValidation
                    text="Use special character & digit"
                    error={complexity.numbers < 1 || complexity.specials < 1}
                    childClassName={
                      forXrayOnboarding
                        ? 'sign-up-form__password-validation-error'
                        : ''
                    }
                    withIcon={false}
                    hideWithoutError
                  />
                </div>
              )}
              {withPasswordConfirmation && (
                <div className="sign-up-form__input-group">
                  <TextInput
                    className="sign-up-form__input"
                    onChange={e => setPasswordControl(e.target.value)}
                    error={!!(passwordControl.length && !passwordMatch)}
                    aria-label="password confirmation"
                    placeholder="Password confirmation"
                    type={passwordVisible[2] ? 'text' : 'password'}
                    autoComplete="off"
                  />
                  <span
                    className="sign-up-form__password-icon"
                    onClick={() =>
                      setPasswordVisible({
                        ...passwordVisible,
                        2: !passwordVisible[2],
                      })
                    }
                  >
                    {passwordVisible[2] ? (
                      <EyeLight color={colors.accentBlue} />
                    ) : (
                      <EyeInvisibleOutlined className="sign-up-form__eye-invisible" />
                    )}
                  </span>
                </div>
              )}
              {!isSigningUpWithToken && !isHighLevel && !hideAgencyCheckbox && (
                <div className="sign-up-form__input-group">
                  {agency && (
                    <Checkbox
                      className="sign-up-form__agency-checkbox"
                      isChecked={useHighLevel}
                      name="highLevel"
                      onToggle={() => setUseHighLevel(!useHighLevel)}
                      label="I use HighLevel"
                      labelHelpTextClassName="sign-up-form__agency_checkbox-label"
                    />
                  )}
                </div>
              )}
              {!isSigningUpWithToken && (
                <div
                  onClick={() =>
                    setIsSmsMarketingChecked(!isSmsMarketingChecked)
                  }
                  className="sign-up-form__sms-marketing"
                >
                  <Checkbox
                    name="sms_marketing"
                    isChecked={isSmsMarketingChecked}
                    className="sign-up-form__sms-marketing-checkbox"
                  />
                  <span className="sign-up-form__sms-marketing-label">
                    <span>Agree to receive SMS marketing messages.</span>
                    <span>
                      Please note that you will receive SMS marketing messages
                      through our website, not by text message. Standard message
                      rates may apply. You can unsubscribe anytime by replying
                      STOP.
                    </span>
                  </span>
                </div>
              )}
              <div className="sign-up-form__cta-btn-wrapper">
                {!isValid && (
                  <div
                    className="sign-up-form__cta-btn-clickable"
                    onClick={() => setShowErrors(true)}
                  />
                )}
                <PrimaryButton
                  disabled={!isValid}
                  className={classNames(
                    css?.submitBtn,
                    'sign-up-form__cta-btn',
                  )}
                  color={ButtonColor.blue}
                  type="submit"
                >
                  {signUpButtonName
                    ? signUpButtonName
                    : beenInvited || hideTrial
                    ? 'Create Account'
                    : 'Sign Up'}
                </PrimaryButton>
              </div>

              <HelpText
                className={classNames(
                  'sign-up-form__footer-actions',
                  css?.helpText,
                )}
              >
                <>
                  By signing up, you agree to Customers.ai’s &nbsp;
                  <Link
                    className="sign-up-form__link"
                    href={Routes.masterServiceAgreement.getLink()}
                    rel="noopener noreferrer"
                    isBlank
                  >
                    Terms of Service
                  </Link>
                  &nbsp;and&nbsp;
                  <Link
                    className="sign-up-form__link"
                    href={Routes.privacyPolicy.getLink()}
                    rel="noopener noreferrer"
                    isBlank
                  >
                    Privacy Policy
                  </Link>
                </>
              </HelpText>
            </>
          )}
        </div>
      </form>
    </Spin>
  );
};
